import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';

import { setAlert } from '../alert/actions';
import WishlistApi from './WishlistApi';
import { DEFAULT_WISHLIST_NAME } from '../../constants/wishlistConfig';
import { parseWishlistItemKey, wishlistItemKey } from './wishlistKey';
import { readWishlistSummary } from './wishlistSync';
import { RootState } from '../';
import { recordBackgroundPoll } from '../../utils/backgroundSyncMetrics';

async function loadWishlistSnapshot(listname: string) {
    const [itemsResponse, summaryResponse] = await Promise.all([
        WishlistApi.getWishListItems(listname),
        WishlistApi.getWishListSummary(listname),
    ]);

    return {
        itemKeys: normalizeItemsPayload(itemsResponse.data),
        fingerprint: readWishlistSummary(summaryResponse.data).fingerprint,
    };
}

export type WishlistActions =
    | { type: 'WISHLIST_FETCH_START' }
    | { type: 'WISHLIST_FETCH_SUCCESS'; payload: { itemKeys: Record<string, boolean>; fingerprint: string } }
    | { type: 'WISHLIST_FETCH_FAIL' }
    | { type: 'WISHLIST_MUTATE_START'; payload: { key: string } }
    | { type: 'WISHLIST_MUTATE_END' }
    | { type: 'WISHLIST_REMOTE_SYNCED' };

function normalizeItemsPayload(data: unknown): Record<string, boolean> {
    const itemKeys: Record<string, boolean> = {};
    if (!Array.isArray(data)) {
        return itemKeys;
    }
    for (const row of data) {
        if (!row || typeof row !== 'object') {
            continue;
        }
        const r = row as Record<string, unknown>;
        const masterRaw = r.masterId !== undefined && r.masterId !== null ? r.masterId : r.masterid;
        const colorRaw = r.colorCode !== undefined && r.colorCode !== null ? r.colorCode : r.colorcode;
        const masterId = masterRaw !== undefined && masterRaw !== null ? String(masterRaw) : '';
        const colorCode = colorRaw !== undefined && colorRaw !== null ? String(colorRaw) : '';
        if (masterId && colorCode) {
            itemKeys[wishlistItemKey(masterId, colorCode)] = true;
        }
    }
    return itemKeys;
}

export const fetchWishlistItems = (): ThunkAction<Promise<void>, {}, {}, AnyAction> => {
    return async (dispatch: ThunkDispatch<{}, {}, AnyAction>): Promise<void> => {
        dispatch({ type: 'WISHLIST_FETCH_START' });
        try {
            const snapshot = await loadWishlistSnapshot(DEFAULT_WISHLIST_NAME);
            dispatch({ type: 'WISHLIST_FETCH_SUCCESS', payload: snapshot });
        } catch {
            dispatch({ type: 'WISHLIST_FETCH_FAIL' });
            dispatch(setAlert('Could not load wishlist', 'error'));
        }
    };
};

export const syncWishlistIfRemoteChanged = (): ThunkAction<Promise<void>, RootState, {}, AnyAction> => {
    return async (dispatch: ThunkDispatch<RootState, {}, AnyAction>, getState): Promise<void> => {
        const wishlistState = getState().wishlist;

        if (wishlistState.loading || wishlistState.mutatingKey) {
            return;
        }

        try {
            const response = await WishlistApi.getWishListSummary(DEFAULT_WISHLIST_NAME);
            recordBackgroundPoll('Wishlist/GetWishListSummary');
            const remote = readWishlistSummary(response.data);
            const localFingerprint = wishlistState.fingerprint;

            if (localFingerprint == null) {
                return;
            }

            if (remote.fingerprint === localFingerprint) {
                return;
            }

            const snapshot = await loadWishlistSnapshot(DEFAULT_WISHLIST_NAME);
            dispatch({ type: 'WISHLIST_REMOTE_SYNCED' });
            dispatch({ type: 'WISHLIST_FETCH_SUCCESS', payload: snapshot });
        } catch {
            // Transient errors retry on next poll.
        }
    };
};

export const toggleWishlistItem = (
    masterId: string,
    colorCode: string,
    isCurrentlyOnList: boolean
): ThunkAction<Promise<void>, {}, {}, AnyAction> => {
    return async (dispatch: ThunkDispatch<{}, {}, AnyAction>): Promise<void> => {
        dispatch({
            type: 'WISHLIST_MUTATE_START',
            payload: { key: wishlistItemKey(masterId, colorCode) },
        });
        try {
            if (isCurrentlyOnList) {
                await WishlistApi.deleteItemFromWishList(
                    DEFAULT_WISHLIST_NAME,
                    masterId,
                    colorCode
                );
                dispatch(setAlert('Removed from wishlist', 'success'));
            } else {
                await WishlistApi.addItemToWishList(
                    DEFAULT_WISHLIST_NAME,
                    masterId,
                    colorCode,
                    ''
                );
                dispatch(setAlert('Added to wishlist', 'success'));
            }
            const res = await WishlistApi.getWishListItems(DEFAULT_WISHLIST_NAME);
            const summaryRes = await WishlistApi.getWishListSummary(DEFAULT_WISHLIST_NAME);
            dispatch({
                type: 'WISHLIST_FETCH_SUCCESS',
                payload: {
                    itemKeys: normalizeItemsPayload(res.data),
                    fingerprint: readWishlistSummary(summaryRes.data).fingerprint,
                },
            });
        } catch {
            dispatch(setAlert('Wishlist could not be updated', 'error'));
        } finally {
            dispatch({ type: 'WISHLIST_MUTATE_END' });
        }
    };
};

/** Clears the list on the server by deleting each item (bulk `ClearWishList` = phase 2). */
export const clearWishlist = (
    listname: string
): ThunkAction<Promise<void>, RootState, {}, AnyAction> => {
    return async (
        dispatch: ThunkDispatch<RootState, {}, AnyAction>,
        getState: () => RootState
    ): Promise<void> => {
        if (!listname) {
            return;
        }
        dispatch({ type: 'WISHLIST_FETCH_START' });
        try {
            const initial = await WishlistApi.getWishListItems(listname);
            const keys = Object.keys(normalizeItemsPayload(initial.data));
            if (!keys.length) {
                dispatch({
                    type: 'WISHLIST_FETCH_SUCCESS',
                    payload:
                        listname === DEFAULT_WISHLIST_NAME
                            ? { itemKeys: {}, fingerprint: 'empty' }
                            : { itemKeys: { ...getState().wishlist.itemKeys }, fingerprint: getState().wishlist.fingerprint || 'empty' },
                });
                return;
            }
            await Promise.all(
                keys.map(async (key) => {
                    const parsed = parseWishlistItemKey(key);
                    if (!parsed) {
                        return;
                    }
                    await WishlistApi.deleteItemFromWishList(
                        listname,
                        parsed.masterId,
                        parsed.colorCode
                    );
                })
            );
            const refreshed = await WishlistApi.getWishListItems(listname);
            const summaryRes = await WishlistApi.getWishListSummary(listname);
            const nextKeys = normalizeItemsPayload(refreshed.data);
            const nextFingerprint = readWishlistSummary(summaryRes.data).fingerprint;
            if (listname === DEFAULT_WISHLIST_NAME) {
                dispatch({ type: 'WISHLIST_FETCH_SUCCESS', payload: { itemKeys: nextKeys, fingerprint: nextFingerprint } });
            } else {
                dispatch({
                    type: 'WISHLIST_FETCH_SUCCESS',
                    payload: { itemKeys: { ...getState().wishlist.itemKeys }, fingerprint: getState().wishlist.fingerprint || 'empty' },
                });
            }
            dispatch(setAlert('Wishlist cleared', 'success'));
        } catch {
            dispatch({ type: 'WISHLIST_FETCH_FAIL' });
            dispatch(setAlert('Could not clear wishlist', 'error'));
            throw new Error('clearWishlist failed');
        }
    };
};
